// src/pages/billing/ViewQuotationPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

const ViewQuotationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [quotation, setQuotation] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if this is a print request
  const isPrintMode = searchParams.get('print') === 'true';

  useEffect(() => {
    if (id) {
      loadQuotation();
      loadBankAccounts();
    }
  }, [id, vendorProfile?.uid]);

  // Auto-print when in print mode
  useEffect(() => {
    if (isPrintMode && quotation && !loading) {
      setTimeout(() => {
        window.print();
        // Close window after printing (if opened as popup)
        setTimeout(() => {
          if (window.opener) {
            window.close();
          }
        }, 1000);
      }, 1000);
    }
  }, [isPrintMode, quotation, loading]);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const quotationRef = doc(db, 'quotations', id);
      const quotationSnap = await getDoc(quotationRef);

      if (!quotationSnap.exists()) {
        setError('Quotation not found');
        return;
      }

      const quotationData = { id: quotationSnap.id, ...quotationSnap.data() };

      // Verify this quotation belongs to the current vendor
      if (quotationData.vendorId !== vendorProfile?.uid) {
        setError('Unauthorized access to quotation');
        return;
      }

      setQuotation(quotationData);
    } catch (error) {
      console.error('Error loading quotation:', error);
      setError('Failed to load quotation');
      showAlert('Failed to load quotation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadBankAccounts = async () => {
    try {
      if (vendorProfile?.uid) {
        const bankCollection = collection(db, 'vendors', vendorProfile.uid, 'bankAccounts');
        const bankSnapshot = await getDocs(bankCollection);
        const accounts = bankSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort accounts to put primary account first
        accounts.sort((a, b) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        });

        setBankAccounts(accounts);
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  const calculateItemTotalWithGST = (item, discountPercentage = 0) => {
    const itemDiscountAmount = (item.amount * discountPercentage) / 100;
    const itemTaxableAmount = item.amount - itemDiscountAmount;
    if (quotation?.quotationType === 'with_gst') {
      const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
      return itemTaxableAmount + totalGSTAmount;
    }
    return itemTaxableAmount;
  };

  const getGstBreakdown = () => {
    if (!quotation) return {};

    const gstBreakdown = {};
    quotation.items?.forEach(item => {
      const rate = item.gstRate || 0;
      const discountPercentage = parseFloat(quotation.discount) || 0;
      const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemTaxableAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });
    return gstBreakdown;
  };

  const getPrimaryBankAccount = () => {
    return bankAccounts.find(account => account.isPrimary) || bankAccounts[0];
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendWhatsApp = () => {
    try {
      const companyName = vendorProfile?.shopName || 'Your Company';
      const quotationUrl = `${window.location.origin}/billing/quotation/${quotation.id}`;
      const itemsCount = quotation.items?.length || 0;
      const totalQuantity = quotation.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

      const message = encodeURIComponent(
        `Dear ${quotation.customerName || 'Customer'},

Thank you for your inquiry!

*Quotation Details:*
📋 Quotation Number: ${quotation.quotationNumber}
🏪 From: ${companyName}
💰 Amount: ${formatCurrency(quotation.totalAmount)}
📅 Date: ${formatDate(quotation.createdAt)}
📦 Items: ${itemsCount} (Total Qty: ${totalQuantity})
${quotation.quotationType === 'with_gst' ? '🧾 GST Quotation' : '📄 Non-GST Quotation'}

View Quotation: ${quotationUrl}

Please contact us for any queries or to proceed with the order.
Best regards,
${companyName}`
      );

      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      showAlert('Failed to generate WhatsApp link', 'error');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !quotation) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{error || 'Quotation not found'}</h3>
            <div className="mt-6">
              <button
                onClick={() => navigate('/billing?tab=quotations')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                Back to Quotations
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const discountAmount = (quotation.subtotal * (parseFloat(quotation.discount) || 0)) / 100;
  const gstBreakdown = getGstBreakdown();
  const primaryBank = getPrimaryBankAccount();

  return (
    <DashboardLayout>
      <style jsx>{`
        @media print {
          body * { visibility: hidden; }
          .print-content, .print-content * { visibility: visible; }
          .print-content { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-content { padding: 20px; font-size: 12px; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6 no-print">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quotation Details</h1>
            <p className="mt-1 text-sm text-gray-600">
              Quotation #{quotation.quotationNumber}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/billing?tab=quotations')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
            <button
              onClick={handleSendWhatsApp}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Send WhatsApp
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
          </div>
        </div>

        <div className="print-content bg-white border border-gray-200 rounded-lg p-8">
          {/* Quotation Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-purple-900 mb-2">QUOTATION</h1>
              <div className="text-sm text-gray-600">
                <p><strong>Quotation #:</strong> {quotation.quotationNumber}</p>
                <p><strong>Date:</strong> {formatDate(quotation.createdAt)}</p>
                <p><strong>Status:</strong>
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {quotation.status?.toUpperCase() || 'ACTIVE'}
                  </span>
                </p>
              </div>
            </div>

            {quotation.quotationType === 'with_gst' && (
              <div className="text-right">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {vendorProfile?.shopName || 'Your Shop Name'}
                </h2>
                <div className="text-sm text-gray-600">
                  <p>{vendorProfile?.address || 'Shop Address'}</p>
                  <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                  <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                  <p>Email: {vendorProfile?.email || 'N/A'}</p>
                  {vendorProfile?.gstNumber && (
                    <p>GST: {vendorProfile.gstNumber}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quote To Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quote To:</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="text-sm">
                <p className="font-medium text-gray-900">{quotation.customerName}</p>
                {quotation.customerEmail && <p className="text-gray-600">{quotation.customerEmail}</p>}
                {quotation.customerPhone && <p className="text-gray-600">{quotation.customerPhone}</p>}
                {quotation.quotationType === 'with_gst' && quotation.customerGST && (
                  <p className="text-gray-600">GST: {quotation.customerGST}</p>
                )}
                {quotation.customerAddress && <p className="text-gray-600 mt-2">{quotation.customerAddress}</p>}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Quotation Items</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Description</th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">HSN</th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Qty</th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Rate (₹)</th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">GST (%)</th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">CGST (₹)</th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">SGST (₹)</th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Amount (₹)</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total (₹)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quotation.items?.filter(item => item.name?.trim() && item.quantity > 0 && item.rate > 0).map((item, index) => {
                    const discountPercentage = parseFloat(quotation.discount) || 0;
                    const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                    const itemTaxableAmount = item.amount - itemDiscountAmount;
                    const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
                    const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                    return (
                      <tr key={index}>
                        <td className="px-3 py-4 border-r">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          {item.productId && <div className="text-xs text-gray-500">ID: {item.productId}</div>}
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.quantity}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                        </td>
                        <td className="px-3 py-4 text-right">
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(totalWithGST)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Combined Totals and Bank Details Section */}
          <div className="flex justify-between mb-8 space-x-6">
            {/* Bank Details Section */}
            {primaryBank ? (
              <div className="flex-1">
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Bank Details
                    {primaryBank.isPrimary && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Primary
                      </span>
                    )}
                  </h5>

                  <div className="flex">
                    <div className="flex-1 space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Account Holder:</span>
                        <span className="font-semibold text-gray-900">{primaryBank.accountHolderName}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Account Number:</span>
                        <span className="font-mono font-semibold text-gray-900">{primaryBank.accountNumber}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">IFSC Code:</span>
                        <span className="font-mono font-semibold text-gray-900">{primaryBank.ifscCode}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Bank Name:</span>
                        <span className="font-semibold text-gray-900 text-right">{primaryBank.bankName}</span>
                      </div>

                      {primaryBank.branchName && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Branch:</span>
                          <span className="font-semibold text-gray-900 text-right">{primaryBank.branchName}</span>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Account Type:</span>
                        <span className="font-semibold text-gray-900 capitalize">{primaryBank.accountType}</span>
                      </div>

                      {primaryBank.upiId && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">UPI ID:</span>
                          <span className="font-mono font-semibold text-gray-900">{primaryBank.upiId}</span>
                        </div>
                      )}
                    </div>

                    {/* UPI QR Code */}
                    {primaryBank.upiScannerURL && (
                      <div className="ml-4 flex flex-col items-center">
                        <p className="text-xs font-medium text-blue-700 mb-2">UPI QR Code</p>
                        <img
                          src={primaryBank.upiScannerURL}
                          alt="UPI QR Code"
                          className="w-48 h-40 object-cover rounded border-2 border-blue-300 shadow-sm"
                        />
                        <p className="text-xs text-blue-600 mt-1 text-center font-medium">Scan to Pay</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h5 className="text-md font-semibold text-gray-900 mb-3">Bank Details</h5>
                  <p className="text-sm text-gray-600">No bank details available</p>
                </div>
              </div>
            )}

            {/* Totals Section */}
            <div className="flex-1">
              <div className="bg-gray-50 p-4 rounded-md border">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(quotation.subtotal)}</span>
                  </div>

                  {quotation.discount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Discount ({quotation.discount}%):</span>
                      <span className="text-sm font-medium text-gray-900">-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}

                  {quotation.quotationType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                    <div className="border-t border-gray-200 pt-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                      {Object.entries(gstBreakdown).map(([rate, data]) => (
                        <div key={rate} className="space-y-1 mb-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center border-t border-gray-200 mt-1 pt-1">
                        <span className="text-sm text-gray-600">Total GST:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(quotation.totalTaxAmount)}</span>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 mt-3 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-purple-900">{formatCurrency(quotation.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          {(quotation.notes || quotation.terms) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {quotation.notes && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h5>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{quotation.notes}</p>
                </div>
              )}

              {quotation.terms && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</h5>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{quotation.terms}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p>Thank you for considering our quotation!</p>
              {quotation.quotationType === 'with_gst' && (
                <p className="mt-2">This is a computer generated quotation.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewQuotationPage;